/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A2/FormA2.tsx.
 */

import React from "react";
import "../../../../../styles/formBase.css";
import "./FormA2.css";
import { FormSubSection } from "../../../../../interfaces/formInterfaces";
import { useA2FormData } from "./hooks/useA2FormData";
import A2Section from "./components/A2Section";
import A2EnergySummary from "./components/A2EnergySummary";
import Button from "../../../../../components/common/Button";
import A2CarbonSummary from "./components/A2CarbonSummary";

interface FormA2Props {
  submissionId?: string;
  onNextStage: () => void;
}

const FormA2: React.FC<FormA2Props> = ({
  submissionId,
  onNextStage,
}) => {
  const {
    formData,
    loading,
    error,
    rawMaterials,
    sectionRows,
    values,
    vehiclesPerRawMaterial,
    fuelsPerRawMaterial,
    addVehicleForMaterial,
    removeVehicleForMaterial,
    addFuelForMaterial,
    removeFuelForMaterial,
    hasSavedOnce,
    isDirty,
    handleChange,
    energySummaryMj,
    energyPerDeclaredFinishedMaterialMj,
    embodiedCarbonTotalKg,
    embodiedCarbonPerFinishedKg,
    perFinishedEnergyUnitLabel,
    perFinishedCarbonUnitLabel,
    buildPayloadAndSave,
    vehicleCategoryEmissionFactors,
    errors,
    fuelDensityCatalog,
    calorificValuesCatalog,
    backgroundFinishedMaterialUnitSymbol,
  } = useA2FormData({ submissionId });

  if (loading) return <p className="loading-text">Loading Form A2...</p>;
  if (!formData) return <p>No form data available.</p>;

  const sections: FormSubSection[] = formData.formSubSections || [];
  const embodiedEnergyFieldsForFuel =
    sections
      .find((s) => s.code === "A2_EMBODIED_ENERGY")
      ?.fields.filter((f) =>
        ["embodiedEnergy", "embodiedEnergy_unit"].includes(f.name)
      ) ?? [];
  const sectionsVisible = sections.filter(
    (s) => s.code !== "A2_EMBODIED_ENERGY"
  );

  return (
    <div className="form-container formA2-container contributor-stage-form">
      <h2 className="form-heading">{formData.name}</h2>

      {sectionsVisible.map((section) => {
        const code =
          section.code || section.name || String(section.id);
        const rowCount = sectionRows[code] || 1;

        return (
          <div key={code} className="form-subsection">
            <h3>{section.name}</h3>

            <A2Section
              section={section}
              rowCount={rowCount}
              rawMaterials={rawMaterials}
              values={values}
              onChange={handleChange}
              vehiclesPerRawMaterial={
                code === "A2_VEHICLE_DETAILS"
                  ? vehiclesPerRawMaterial
                  : undefined
              }
              onAddVehicleForMaterial={
                code === "A2_VEHICLE_DETAILS"
                  ? addVehicleForMaterial
                  : undefined
              }
              onRemoveVehicleForMaterial={
                code === "A2_VEHICLE_DETAILS"
                  ? removeVehicleForMaterial
                  : undefined
              }
              fuelsPerRawMaterial={
                code === "A2_FUEL_TRANSPORT"
                  ? fuelsPerRawMaterial
                  : undefined
              }
              onAddFuelForMaterial={
                code === "A2_FUEL_TRANSPORT"
                  ? addFuelForMaterial
                  : undefined
              }
              onRemoveFuelForMaterial={
                code === "A2_FUEL_TRANSPORT"
                  ? removeFuelForMaterial
                  : undefined
              }
              embodiedEnergyFieldsForFuel={
                code === "A2_FUEL_TRANSPORT"
                  ? embodiedEnergyFieldsForFuel
                  : undefined
              }
              vehicleCategoryEmissionFactors={
                code === "A2_VEHICLE_DETAILS" ||
                code === "A2_EMBODIED_CARBON"
                  ? vehicleCategoryEmissionFactors
                  : undefined
              }
              errors={errors}
              formData={formData}
              fuelDensityCatalog={fuelDensityCatalog}
              calorificValuesCatalog={calorificValuesCatalog}
              backgroundFinishedMaterialUnitSymbol={
                backgroundFinishedMaterialUnitSymbol
              }
            />

            {code === "A2_FUEL_TRANSPORT" && (
              <A2EnergySummary
                energyMj={energySummaryMj}
                energyPerDeclaredFinishedMj={
                  energyPerDeclaredFinishedMaterialMj
                }
                perFinishedUnitLabel={perFinishedEnergyUnitLabel}
              />
            )}

            {code === "A2_EMBODIED_CARBON" && (
              <A2CarbonSummary
                totalKgCo2e={embodiedCarbonTotalKg}
                perFinishedKgCo2e={embodiedCarbonPerFinishedKg}
                perFinishedUnitLabel={perFinishedCarbonUnitLabel}
              />
            )}
          </div>
        );
      })}

      <div className="button-row">
        <Button
          label="Save"
          type="button"
          disabled={loading || !isDirty}
          onClick={buildPayloadAndSave}
        />
        <Button
          label="Next"
          type="button"
          disabled={loading || isDirty}
          onClick={async () => {
            if (loading || isDirty) return;
            if (!hasSavedOnce) {
              const ok = await buildPayloadAndSave();
              if (!ok) return;
            }
            onNextStage();
          }}
        />
      </div>
    </div>
  );
};

/** React component — Form A2. */
export default FormA2;